import type { CardCandidate, CardGame } from './types'
import { getApiUrl } from './runtime'

export type CoachMode = 'ask' | 'review' | 'combos' | 'improve' | 'build'
export type CoachScope = 'deck' | 'library'

export type CoachCardInput = Pick<
  CardCandidate,
  | 'sourceId'
  | 'name'
  | 'setCode'
  | 'collectorNumber'
  | 'category'
  | 'colors'
  | 'manaCost'
  | 'manaValue'
  | 'rulesText'
  | 'typeLine'
  | 'colorIdentity'
  | 'legalities'
  | 'subtypes'
  | 'hp'
  | 'evolvesFrom'
  | 'retreatCost'
  | 'attacks'
  | 'abilities'
  | 'regulationMark'
> & {
  quantity: number
}

export interface CoachRequest {
  requestId: string
  game: CardGame
  scope: CoachScope
  mode: CoachMode
  deckName: string
  format: string
  commanderName: string
  goal: string
  conversation: Array<{
    role: 'user' | 'assistant'
    content: string
  }>
  deckCards: CoachCardInput[]
  collectionCards: CoachCardInput[]
}

export interface CoachCreditStatus {
  balance: number
  plan: string
  planExpiresAt?: string | null
  unlimited: boolean
  monthlyFreeCredits: number
  monthlyFreeGrantDays: number
}

export interface CoachCreditUsage {
  cost: number
  deducted: number
  unlimited: boolean
}

export interface CoachLegalityIssue {
  card: string
  issue: string
}

export interface CoachDeterministicResult {
  game: CardGame
  format: string
  commanderName: string
  summary: string
  metrics: Record<string, number>
  strengths: string[]
  concerns: string[]
  legalityIssues: CoachLegalityIssue[]
  ownershipShortfalls: Array<{
    name: string
    needed: number
    owned: number
  }>
}

export interface CoachSynergy {
  cards: string[]
  explanation: string
  confidence: number
  score?: number
  tier?: 'Strong synergy' | 'Engine' | 'Useful synergy' | 'Minor interaction'
}

export interface CoachCardChange {
  name: string
  quantity: number
}

export interface CoachRecommendationGroup {
  label: string
  totalQuantity: number
  distinctCards: number
  cards: CoachCardChange[]
}

export interface CoachCardRef {
  sourceId: string
  name: string
}

export interface CoachRecommendation {
  title: string
  reason: string
  add: CoachCardChange[]
  remove: CoachCardChange[]
  groups?: CoachRecommendationGroup[]
  confidence: number
}

export interface CoachResult {
  ok: boolean
  error?: string
  modelStatus: 'ready' | 'partial' | 'busy' | 'unavailable'
  model: string
  mode: CoachMode | 'fallback'
  deterministic: CoachDeterministicResult
  analysisScope: {
    deckCards: number
    collectionCards: number
    collectionCardsMatched?: number
    collectionCardsHydrated: number
    collectionCardsModeled: number
    factsResolved: number
  }
  summary: string
  strengths: string[]
  concerns: string[]
  cardRefs: CoachCardRef[]
  synergies: CoachSynergy[]
  ownedRecommendations: CoachRecommendation[]
  missingRecommendations: CoachRecommendation[]
  warnings: string[]
  credits?: CoachCreditStatus
  creditUsage?: CoachCreditUsage
}

export interface CoachCommanderSuggestion {
  name: string
  sourceId: string
  score: number
  supportCount: number
  reason: string
}

export function buildCoachCardInput(
  card: CardCandidate,
  quantity: number,
): CoachCardInput {
  return {
    sourceId: card.sourceId,
    name: card.name,
    setCode: card.setCode,
    collectorNumber: card.collectorNumber,
    category: card.category,
    colors: card.colors,
    manaCost: card.manaCost,
    manaValue: card.manaValue,
    rulesText: card.rulesText,
    typeLine: card.typeLine,
    colorIdentity: card.colorIdentity,
    legalities: card.legalities,
    subtypes: card.subtypes,
    hp: card.hp,
    evolvesFrom: card.evolvesFrom,
    retreatCost: card.retreatCost,
    attacks: card.attacks,
    abilities: card.abilities,
    regulationMark: card.regulationMark,
    quantity,
  }
}

export async function requestCoach(
  request: CoachRequest,
  accountToken: string,
): Promise<CoachResult> {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), 181001)

  try {
    const response = await fetch(getApiUrl('/api/coach/analyze'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accountToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
      signal: controller.signal,
    })
    const payload = (await response.json()) as CoachResult

    if (!response.ok || !payload.ok) {
      throw new Error(payload.error || 'Coach could not analyze this deck.')
    }

    return payload
  } finally {
    window.clearTimeout(timeoutId)
  }
}

export async function requestCommanderSuggestions(
  request: {
    requestId: string
    colors: string[]
    style: string
    collectionCards: CoachCardInput[]
  },
  accountToken: string,
): Promise<CoachCommanderSuggestion[]> {
  const response = await fetch(getApiUrl('/api/coach/commander-suggestions'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accountToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  })
  const payload = (await response.json()) as {
    ok: boolean
    error?: string
    suggestions?: CoachCommanderSuggestion[]
  }
  if (!response.ok || !payload.ok) {
    throw new Error(payload.error || 'Coach could not suggest a commander.')
  }
  return payload.suggestions ?? []
}

export async function requestCoachCreditStatus(
  accountToken: string,
): Promise<CoachCreditStatus> {
  const response = await fetch(getApiUrl('/api/credits/status'), {
    headers: {
      Authorization: `Bearer ${accountToken}`,
    },
  })
  const payload = (await response.json()) as {
    ok: boolean
    error?: string
    credits?: CoachCreditStatus
  }
  if (!response.ok || !payload.ok || !payload.credits) {
    throw new Error(payload.error || 'Could not load Coach credits.')
  }
  return payload.credits
}

export async function recordMicrosoftStoreCoachPurchase(
  accountToken: string,
  purchase: {
    productId?: string
    storeId?: string
    purchaseId?: string
    expiresAt?: string
    credits?: number
    status?: string
  },
): Promise<CoachCreditStatus> {
  const response = await fetch(getApiUrl('/api/purchases/microsoft-store'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accountToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(purchase),
  })
  const payload = (await response.json()) as {
    ok: boolean
    error?: string
    credits?: CoachCreditStatus
  }
  if (!response.ok || !payload.ok || !payload.credits) {
    throw new Error(payload.error || 'Could not apply Microsoft Store purchase.')
  }
  return payload.credits
}

export async function recordGooglePlayCoachPurchase(
  accountToken: string,
  purchase: {
    productId?: string
    purchaseToken?: string
    orderId?: string
    packageName?: string
    purchaseState?: string
    acknowledged?: boolean
  },
): Promise<CoachCreditStatus> {
  const response = await fetch(getApiUrl('/api/purchases/google-play'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accountToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(purchase),
  })
  const payload = (await response.json()) as {
    ok: boolean
    error?: string
    credits?: CoachCreditStatus
  }
  if (!response.ok || !payload.ok || !payload.credits) {
    throw new Error(payload.error || 'Could not apply Google Play purchase.')
  }
  return payload.credits
}
